/**
 * Action Templates
 * Maps natural language phrases to Redux actions
 * Based on analysis of src/redux/actions/types.js
 */

import { ActionTemplate } from './semanticActionMapper';
import * as types from '../redux/actions/types';
import { getSchemaForAction } from './schemaCache';
import {
	SHOW_SUCCESS_TOAST,
	SHOW_ERROR_TOAST,
	SHOW_WARNING_TOAST,
	ADD_CALENDAR_ENTRY,
	ADD_NEW_PROJECT,
	REACT_NAVIGATE,
	CHANGE_THEME,
	TOGGLE_FOCUS_MODE,
	TOGGLE_ADVANCED_MODE,
	OPEN_QUICK_ACTIONS,
	MENU_OPEN,
	MENU_CLOSE,
	OPEN_CONTEXT,
	NOTIFICATIONS_ACKNOWLEDGED,
	SHOW_INFO_TOAST,
	SHOW_LOADING_TOAST,
	SHOW_CONFIRMATION_TOAST,
	CREATE_REPORT_DASHBOARD,
} from '../redux/actions/types';
// Modal Actions
export const modalActionTemplates: ActionTemplate[] = [
	{
		intent: types.SHOW_HELP_MODAL,
		phrases: [
			'show help',
			'open help',
			'I need help',
			'help me',
			'help documentation',
			'show documentation',
			'how do I use this',
		],
		reduxAction: () => ({ type: types.SHOW_HELP_MODAL }),
		category: 'modal',
		requiresConfirmation: false,
		destructive: false,
		description: 'Open help documentation',
	},
	{
		intent: types.SHOW_PROJECT_MODAL,
		phrases: [
			'create project',
			'new project',
			'add project',
			'start project',
			'open project modal',
			'make a project',
		],
		reduxAction: (data) => ({
			type: types.SHOW_PROJECT_MODAL,
			payload: data,
		}),
		actionSequence: [
			// First navigate to projects page
			() => ({
				type: types.REACT_NAVIGATE,
				payload: {
					screen: 'ProjectMenu',
					stack: 'Projects',
				},
			}),
			// Then open the project modal with extracted data
			(data) => ({ type: types.SHOW_PROJECT_MODAL, payload: data }),
		],
		category: 'modal',
		requiresConfirmation: false,
		destructive: false,
		requiresData: true,
		schema: {
			title: {
				type: 'string',
				validation: { required: true },
				label: 'Project Title',
				placeholder: 'Enter project title',
			},
			description: {
				type: 'string',
				label: 'Description',
				placeholder: 'Project description',
			},
			date_range: {
				type: 'dateRange',
				label: 'Project Duration',
			},
		},
		schemaConfig: {
			buttonLabel: 'Create Project',
			columns: 1,
		},
		description: 'Navigate to projects and create new project',
	},
	{
		intent: types.SHOW_CALENDAR_ENTRY_MODAL,
		phrases: [
			'add calendar event',
			'create event',
			'schedule meeting',
			'add to calendar',
			'new calendar entry',
			'schedule appointment',
		],
		reduxAction: () => ({ type: types.SHOW_CALENDAR_ENTRY_MODAL }),
		category: 'modal',
		requiresConfirmation: false,
		destructive: false,
		description: 'Open calendar entry form',
	},
	{
		intent: 'CREATE_EVENT_WITH_PROJECT',
		phrases: [
			'create event and project',
			'schedule meeting with project',
			'plan event with project',
			'organize event with project',
			'setup meeting and project',
		],
		reduxAction: (data) => ({
			type: 'CREATE_EVENT_WITH_PROJECT',
			payload: data,
		}),
		actionSequence: [
			// Step 1: Navigate to calendar
			() => ({
				type: types.REACT_NAVIGATE,
				payload: { screen: 'CalendarScreen', stack: 'CalendarStack' },
			}),
			// Step 2: Create calendar event
			(data) => ({
				type: types.ADD_CALENDAR_ENTRY,
				payload: {
					title: data.title,
					date: data.date,
					duration: data.duration,
					description: data.description,
				},
			}),
			// Step 3: Create related project
			(data) => ({
				type: types.ADD_NEW_PROJECT,
				payload: {
					name: `Project for ${data.title}`,
					dueDate: data.date,
					description: `Auto-created for calendar event: ${data.description || data.title}`,
					priority: data.priority || 'medium',
				},
			}),
			// Step 4: Show success notification
			() => ({
				type: types.SHOW_SUCCESS_TOAST,
				payload: 'Event and project created successfully!',
			}),
		],
		category: 'workflow',
		requiresConfirmation: true,
		destructive: false,
		requiresData: true,
		schema: {
			title: {
				type: 'string',
				validation: { required: true },
				label: 'Event Title',
				placeholder: 'Enter event title',
			},
			date: {
				type: 'date',
				validation: { required: true },
				label: 'Event Date',
			},
			duration: {
				type: 'number',
				validation: { required: true, min: 15 },
				label: 'Duration (minutes)',
				placeholder: '60',
			},
			description: {
				type: 'string',
				label: 'Description',
				placeholder: 'Event description',
			},
			priority: {
				type: 'select',
				label: 'Project Priority',
				options: [
					{ value: 'low', label: 'Low' },
					{ value: 'medium', label: 'Medium' },
					{ value: 'high', label: 'High' },
				],
				defaultValue: 'medium',
			},
		},
		schemaConfig: {
			buttonLabel: 'Create Event & Project',
			columns: 1,
		},
		description: 'Create calendar event and related project',
	},
	{
		intent: types.SHOW_MESSAGE_MODAL,
		phrases: [
			'show messages',
			'open messages',
			'check messages',
			'view messages',
			'read messages',
		],
		reduxAction: () => ({ type: types.SHOW_MESSAGE_MODAL }),
		category: 'modal',
		requiresConfirmation: false,
		destructive: false,
		description: 'Open messages',
	},
	{
		intent: types.SHOW_NOTIFICATIONS_PANEL,
		phrases: [
			'show notifications',
			'check notifications',
			'open notifications',
			'view notifications',
			'notification panel',
		],
		reduxAction: () => ({ type: types.SHOW_NOTIFICATIONS_PANEL }),
		category: 'modal',
		requiresConfirmation: false,
		destructive: false,
		description: 'Open notifications panel',
	},
	{
		intent: types.SHOW_STICKY_NOTES_MODAL,
		phrases: [
			'show notes',
			'open notes',
			'sticky notes',
			'view notes',
			'my notes',
		],
		reduxAction: () => ({ type: types.SHOW_STICKY_NOTES_MODAL }),
		category: 'modal',
		requiresConfirmation: false,
		destructive: false,
		description: 'Open sticky notes',
	},
];

// Data Operation Templates
export const dataActionTemplates: ActionTemplate[] = [
	{
		intent: types.PENDING_REFRESH,
		phrases: [
			'refresh',
			'reload',
			'refresh data',
			'reload data',
			'update data',
			'refresh the page',
			'reload the page',
		],
		reduxAction: () => ({ type: types.PENDING_REFRESH, payload: true }),
		category: 'data',
		requiresConfirmation: false,
		destructive: false,
		description: 'Refresh current data',
	},
	{
		intent: 'MANAGE_DATA_SEQUENCE',
		phrases: [
			'manage data',
			'view data',
			'open data page',
			'show data management',
			'go to data',
		],
		reduxAction: () => ({ type: types.OPEN_DATA }),
		actionSequence: [
			// Navigate to data page
			() => ({
				type: types.REACT_NAVIGATE,
				payload: { screen: 'DataScreen', stack: 'DataStack' },
			}),
		],
		category: 'navigation',
		requiresConfirmation: false,
		destructive: false,
		description: 'Navigate to data management page',
	},
	{
		intent: types.CREATE_REPORT_DASHBOARD,
		phrases: [
			'create report',
			'build dashboard',
			'show reports',
			'generate dashboard',
			'create reports',
			'build report',
			'make dashboard',
			'create a dashboard',
			'show me reports',
			'generate reports',
		],
		reduxAction: () => ({ type: types.CREATE_REPORT_DASHBOARD }),
		category: 'data_visualization',
		requiresConfirmation: false,
		destructive: false,
		description: 'Create a report dashboard from data sources',
	},
	{
		intent: 'CREATE_DOCUMENT',
		phrases: [
			'create business plan',
			'make a pitch deck',
			'generate proposal',
			'create document',
			'build presentation',
			'design document',
			'create a business plan',
			'make pitch deck',
			'generate business plan',
			'create proposal',
			'build document',
			'design presentation',
		],
		reduxAction: (data) => ({ type: 'CREATE_DOCUMENT', payload: data }),
		category: 'data',
		requiresConfirmation: false,
		destructive: false,
		requiresData: true,
		schema: {
			documentType: {
				type: 'select',
				label: 'Document Type',
				options: ['business_plan', 'pitch_deck', 'proposal', 'report', 'other'],
				validation: { required: true },
			},
			title: {
				type: 'string',
				label: 'Document Title',
				validation: { required: true },
				placeholder: 'Enter document title',
			},
			description: {
				type: 'string',
				label: 'Description/Requirements',
				placeholder: 'Describe what you want in the document',
			},
		},
		schemaConfig: {
			buttonLabel: 'Create Document',
			columns: 1,
		},
		description: 'Create a new document (business plan, pitch deck, etc.)',
	},
	{
		intent: types.ADD_NEW_PROJECT,
		phrases: [
			'create new project with details',
			'add project with information',
			'start new project with data',
		],
		reduxAction: (data) => ({ type: types.ADD_NEW_PROJECT, payload: data }),
		actionSequence: [
			// First navigate to projects page
			() => ({
				type: types.REACT_NAVIGATE,
				payload: {
					screen: 'ProjectManagementScreen',
					stack: 'ProjectStack',
				},
			}),
			// Then open the project modal
			() => ({ type: types.SHOW_PROJECT_MODAL }),
			// Then add the project with data
			(data) => ({ type: types.ADD_NEW_PROJECT, payload: data }),
		],
		category: 'data',
		requiresConfirmation: true,
		destructive: false,
		requiresData: true,
		schema: {
			projectName: {
				type: 'string',
				validation: { required: true },
				label: 'Project Name',
				placeholder: 'Enter project name',
			},
			description: {
				type: 'string',
				label: 'Description',
				placeholder: 'Project description',
			},
			dueDate: {
				type: 'date',
				label: 'Due Date',
			},
		},
		schemaConfig: {
			buttonLabel: 'Create Project',
			staged: false,
			columns: 1,
		},
		description: 'Navigate to projects and create a new project',
	},
];

// UI State Templates
export const uiActionTemplates: ActionTemplate[] = [
	{
		intent: types.CHANGE_THEME,
		phrases: [
			'change theme',
			'switch theme',
			'toggle theme',
			'dark mode',
			'light mode',
			'switch to dark mode',
			'switch to light mode',
		],
		reduxAction: () => ({ type: types.CHANGE_THEME }),
		category: 'ui',
		requiresConfirmation: false,
		destructive: false,
		description: 'Toggle light/dark theme',
	},
	{
		intent: types.TOGGLE_FOCUS_MODE,
		phrases: [
			'focus mode',
			'enable focus mode',
			'distraction free',
			'hide distractions',
			'turn on focus mode',
		],
		reduxAction: () => ({ type: types.TOGGLE_FOCUS_MODE }),
		category: 'ui',
		requiresConfirmation: false,
		destructive: false,
		description: 'Toggle focus mode',
	},
	{
		intent: types.TOGGLE_ADVANCED_MODE,
		phrases: [
			'advanced mode',
			'enable advanced mode',
			'show advanced options',
			'advanced settings',
		],
		reduxAction: () => ({ type: types.TOGGLE_ADVANCED_MODE }),
		category: 'ui',
		requiresConfirmation: false,
		destructive: false,
		description: 'Toggle advanced mode',
	},
	{
		intent: types.OPEN_QUICK_ACTIONS,
		phrases: [
			'open quick actions',
			'show quick actions',
			'quick menu',
			'shortcuts',
		],
		reduxAction: () => ({ type: types.OPEN_QUICK_ACTIONS }),
		category: 'ui',
		requiresConfirmation: false,
		destructive: false,
		description: 'Open quick actions menu',
	},
];

// Navigation Templates
export const navigationActionTemplates: ActionTemplate[] = [
	{
		intent: types.MENU_OPEN,
		phrases: [
			'open menu',
			'show menu',
			'open navigation',
			'show navigation',
		],
		reduxAction: () => ({ type: types.MENU_OPEN }),
		category: 'navigation',
		requiresConfirmation: false,
		destructive: false,
		description: 'Open main menu',
	},
	{
		intent: types.MENU_CLOSE,
		phrases: ['close menu', 'hide menu', 'close navigation'],
		reduxAction: () => ({ type: types.MENU_CLOSE }),
		category: 'navigation',
		requiresConfirmation: false,
		destructive: false,
		description: 'Close main menu',
	},
	{
		intent: types.OPEN_CONTEXT,
		phrases: [
			'open context',
			'show context',
			'context panel',
			'show context panel',
		],
		reduxAction: () => ({ type: types.OPEN_CONTEXT }),
		category: 'navigation',
		requiresConfirmation: false,
		destructive: false,
		description: 'Open context panel',
	},
];

// Notification Templates
export const notificationActionTemplates: ActionTemplate[] = [
	{
		intent: types.CLEAR_NOTIFICATIONS,
		phrases: [
			'clear notifications',
			'delete notifications',
			'remove all notifications',
			'clear all notifications',
		],
		reduxAction: () => ({ type: types.CLEAR_NOTIFICATIONS }),
		category: 'ui',
		requiresConfirmation: true,
		destructive: true,
		description: 'Clear all notifications',
	},
	{
		intent: types.NOTIFICATIONS_ACKNOWLEDGED,
		phrases: [
			'mark notifications as read',
			'acknowledge notifications',
			'read notifications',
			'mark all as read',
		],
		reduxAction: () => ({ type: types.NOTIFICATIONS_ACKNOWLEDGED }),
		category: 'ui',
		requiresConfirmation: false,
		destructive: false,
		description: 'Mark all notifications as read',
	},
];

// Combine all templates
export const allActionTemplates: ActionTemplate[] = [
	...modalActionTemplates,
	...dataActionTemplates,
	...uiActionTemplates,
	...navigationActionTemplates,
	...notificationActionTemplates,
];

// Export by category for organized registration
export const actionTemplatesByCategory = {
	modal: modalActionTemplates,
	data: dataActionTemplates,
	ui: uiActionTemplates,
	navigation: navigationActionTemplates,
	notification: notificationActionTemplates,
};

export default allActionTemplates;
